import { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { ScrollArea } from '@/components/ui/scroll-area';
import {
  Users,
  Building2,
  Target,
  FileText,
  Gift,
  BarChart3,
  Settings,
  Search,
  Plus,
  Edit,
  Eye,
  Download,
  Upload,
  Mail,
  Calendar,
  DollarSign,
  Briefcase,
  ChevronRight,
  CheckCircle,
  AlertCircle,
  Info,
  Lightbulb,
  BookOpen,
  UserPlus,
  Workflow,
  Layers,
  PieChart,
  TrendingUp,
  FileSpreadsheet,
  Link,
  Shield,
  HelpCircle,
} from 'lucide-react';

interface DocSection {
  id: string;
  title: string;
  icon: React.ReactNode;
  content: () => React.ReactNode;
}

export default function Docs() {
  const [activeSection, setActiveSection] = useState('getting-started');

  const sections: DocSection[] = [
    {
      id: 'getting-started',
      title: '🚀 Start Here!',
      icon: <BookOpen className="w-4 h-4" />,
      content: () => <GettingStartedContent />,
    },
    {
      id: 'contacts',
      title: '👥 Working with People',
      icon: <Users className="w-4 h-4" />,
      content: () => <ContactsContent />,
    },
    {
      id: 'organizations',
      title: '🏢 Managing Companies',
      icon: <Building2 className="w-4 h-4" />,
      content: () => <OrganizationsContent />,
    },
    {
      id: 'opportunities',
      title: '💼 Tracking Deals',
      icon: <Target className="w-4 h-4" />,
      content: () => <OpportunitiesContent />,
    },
    {
      id: 'projects',
      title: '📋 Running Projects',
      icon: <Briefcase className="w-4 h-4" />,
      content: () => <ProjectsContent />,
    },
    {
      id: 'land-expand',
      title: '📈 Growing Accounts',
      icon: <TrendingUp className="w-4 h-4" />,
      content: () => <LandExpandContent />,
    },
    {
      id: 'gifts',
      title: '🎁 Gift Management',
      icon: <Gift className="w-4 h-4" />,
      content: () => <GiftsContent />,
    },
    {
      id: 'analytics',
      title: '📊 Reports & Insights',
      icon: <BarChart3 className="w-4 h-4" />,
      content: () => <AnalyticsContent />,
    },
    {
      id: 'import-export',
      title: '📥 Import/Export Data',
      icon: <FileSpreadsheet className="w-4 h-4" />,
      content: () => <ImportExportContent />,
    },
    {
      id: 'invoices',
      title: '💰 Creating Invoices',
      icon: <DollarSign className="w-4 h-4" />,
      content: () => <InvoicesContent />,
    },
    {
      id: 'documents',
      title: 'Document Management',
      icon: <FileText className="w-4 h-4" />,
      content: () => <DocumentsContent />,
    },
    {
      id: 'automation',
      title: 'Workflow Automation',
      icon: <Workflow className="w-4 h-4" />,
      content: () => <AutomationContent />,
    },
    {
      id: 'communication-hub',
      title: 'Communication Hub',
      icon: <Mail className="w-4 h-4" />,
      content: () => <CommunicationHubContent />,
    },
    {
      id: 'financial',
      title: 'Financial Management',
      icon: <DollarSign className="w-4 h-4" />,
      content: () => <FinancialContent />,
    },
    {
      id: 'user-management',
      title: 'User Management',
      icon: <Shield className="w-4 h-4" />,
      content: () => <UserManagementContent />,
    },
    {
      id: 'settings',
      title: 'Settings & Preferences',
      icon: <Settings className="w-4 h-4" />,
      content: () => <SettingsContent />,
    },
    {
      id: 'integrations',
      title: 'Integrations',
      icon: <Link className="w-4 h-4" />,
      content: () => <IntegrationsContent />,
    },
    {
      id: 'best-practices',
      title: 'Best Practices',
      icon: <Lightbulb className="w-4 h-4" />,
      content: () => <BestPracticesContent />,
    },
    {
      id: 'troubleshooting',
      title: '❓ Need Help?',
      icon: <HelpCircle className="w-4 h-4" />,
      content: () => <TroubleshootingContent />,
    },
    {
      id: 'testing-checklist',
      title: '✅ Testing Checklist',
      icon: <CheckCircle className="w-4 h-4" />,
      content: () => <TestingChecklistContent />,
    },
  ];

  // Handle URL hash navigation
  useEffect(() => {
    const hash = window.location.hash.replace('#', '');
    if (hash && sections.some(s => s.id === hash)) {
      setActiveSection(hash);
    }
  }, [sections]);

  const handleSectionChange = (sectionId: string) => {
    setActiveSection(sectionId);
    window.history.pushState(null, '', `#${sectionId}`);
  };

  return (
    <div className="container mx-auto py-6 space-y-6">
      <div className="mb-6">
        <h1 className="text-3xl font-bold">User Guide</h1>
        <p className="text-muted-foreground">Everything you need to know about using Spherical Manager CRM</p>
      </div>
      
      <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
        {/* Sidebar Navigation */}
        <div className="lg:col-span-1">
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Documentation</CardTitle>
              <CardDescription>Learn how to use Spherical Manager</CardDescription>
            </CardHeader>
            <CardContent className="p-0">
              <ScrollArea className="h-[calc(100vh-300px)]">
                <div className="space-y-1 p-4">
                  {sections.map((section) => (
                    <Button
                      key={section.id}
                      variant={activeSection === section.id ? 'secondary' : 'ghost'}
                      className="w-full justify-start"
                      onClick={() => handleSectionChange(section.id)}
                    >
                      {section.icon}
                      <span className="ml-2">{section.title}</span>
                    </Button>
                  ))}
                </div>
              </ScrollArea>
            </CardContent>
          </Card>
        </div>

        {/* Content Area */}
        <div className="lg:col-span-3">
          <Card>
            <ScrollArea className="h-[calc(100vh-200px)]">
              {sections.find((s) => s.id === activeSection)?.content()}
            </ScrollArea>
          </Card>
        </div>
      </div>
    </div>
  );
}

// Getting Started Content
function GettingStartedContent() {
  return (
    <div className="p-6 space-y-6">
      <div>
        <h2 className="text-2xl font-bold mb-4">Welcome to Spherical Manager! 👋</h2>
        <p className="text-lg mb-4">
          Let's get you started with managing your client relationships effectively.
        </p>
        <div className="bg-blue-50 dark:bg-blue-950 p-4 rounded-lg border border-blue-200 dark:border-blue-800">
          <p className="text-sm">
            <strong>💡 Tip:</strong> This guide is designed for everyone - no technical knowledge needed! 
            Follow along step-by-step and try each feature as you read about it.
          </p>
        </div>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <UserPlus className="w-5 h-5" />
            Your First 10 Minutes - Let's Do This Together! 🚀
          </CardTitle>
          <CardDescription>
            Follow these simple steps to get comfortable with the system
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          <div className="flex items-start gap-3">
            <Badge className="mt-1 bg-green-500">Step 1</Badge>
            <div className="flex-1">
              <h4 className="font-semibold mb-2">Log In to Your Account</h4>
              <p className="text-sm text-muted-foreground mb-3">
                Go to the login page and enter your email and password. If you don't have them yet, 
                ask your manager or admin for help.
              </p>
              <div className="bg-gray-50 dark:bg-gray-900 p-3 rounded-md">
                <p className="text-xs font-mono">
                  <strong>Try it now:</strong> After logging in, you'll see the Dashboard
                </p>
              </div>
            </div>
          </div>
          
          <Separator />
          
          <div className="flex items-start gap-3">
            <Badge className="mt-1 bg-blue-500">Step 2</Badge>
            <div className="flex-1">
              <h4 className="font-semibold mb-2">Look Around Your Dashboard</h4>
              <p className="text-sm text-muted-foreground mb-3">
                The Dashboard is your home base. It shows:
              </p>
              <ul className="list-disc list-inside text-sm text-muted-foreground space-y-1 mb-3">
                <li>Important numbers (like total clients and active projects)</li>
                <li>Recent activities (who did what and when)</li>
                <li>Quick access buttons to create new things</li>
              </ul>
              <div className="bg-yellow-50 dark:bg-yellow-950 p-3 rounded-md">
                <p className="text-xs">
                  <strong>Fun fact:</strong> You can drag and drop the boxes on your dashboard to arrange them however you like!
                </p>
              </div>
            </div>
          </div>
          
          <Separator />
          
          <div className="flex items-start gap-3">
            <Badge className="mt-1 bg-purple-500">Step 3</Badge>
            <div className="flex-1">
              <h4 className="font-semibold mb-2">Find Your Way Around</h4>
              <p className="text-sm text-muted-foreground mb-3">
                Look at the left side of your screen - that's your navigation menu. Think of it like a table of contents:
              </p>
              <ul className="list-disc list-inside text-sm text-muted-foreground space-y-1 mb-3">
                <li><strong>Organizations</strong> = Your client companies</li>
                <li><strong>Contacts</strong> = People you work with</li>
                <li><strong>Opportunities</strong> = Potential deals</li>
                <li><strong>Projects</strong> = Active work you're doing</li>
              </ul>
              <div className="bg-green-50 dark:bg-green-950 p-3 rounded-md">
                <p className="text-xs">
                  <strong>Try this:</strong> Click on "Organizations" in the sidebar right now to see your client list!
                </p>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Lightbulb className="w-5 h-5" />
            Common Things You'll Do Every Day
          </CardTitle>
          <CardDescription>
            Here are the tasks you'll probably use most often
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="border-l-4 border-blue-500 pl-4">
            <h4 className="font-semibold flex items-center gap-2 mb-2">
              <Search className="w-4 h-4" />
              Finding Information
            </h4>
            <ul className="text-sm text-muted-foreground space-y-1">
              <li>• <strong>Quick Search:</strong> Use the search bar at the top to find any client, contact, or project</li>
              <li>• <strong>Filters:</strong> Each page has filters to narrow down your results</li>
              <li>• <strong>Recent Items:</strong> Check your dashboard for things you've worked on recently</li>
            </ul>
          </div>
          
          <div className="border-l-4 border-green-500 pl-4">
            <h4 className="font-semibold flex items-center gap-2 mb-2">
              <Plus className="w-4 h-4" />
              Creating New Records
            </h4>
            <ul className="text-sm text-muted-foreground space-y-1">
              <li>• <strong>New Client:</strong> Organizations → Click "New Organization" button</li>
              <li>• <strong>New Contact:</strong> Contacts → Click "Add Contact" button</li>
              <li>• <strong>New Deal:</strong> Opportunities → Click "Create Opportunity" button</li>
            </ul>
          </div>
          
          <div className="border-l-4 border-purple-500 pl-4">
            <h4 className="font-semibold flex items-center gap-2 mb-2">
              <Edit className="w-4 h-4" />
              Updating Information
            </h4>
            <ul className="text-sm text-muted-foreground space-y-1">
              <li>• Click on any record to view its details</li>
              <li>• Look for the "Edit" button (usually in the top right)</li>
              <li>• Make your changes and click "Save"</li>
            </ul>
          </div>
          
          <div className="bg-orange-50 dark:bg-orange-950 p-4 rounded-lg mt-4">
            <p className="text-sm">
              <strong>🎯 Quick Exercise:</strong> Try creating a test organization right now! 
              Go to Organizations → New Organization → Fill in a fake company name → Save. 
              You can delete it later - this is just practice!
            </p>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

// Contacts Content
function ContactsContent() {
  return (
    <div className="p-6 space-y-6">
      <div>
        <h2 className="text-2xl font-bold mb-4">Working with People (Contacts) 👥</h2>
        <p className="text-lg mb-4">
          Let's learn how to keep track of all the people you work with!
        </p>
        <div className="bg-purple-50 dark:bg-purple-950 p-4 rounded-lg border border-purple-200 dark:border-purple-800">
          <p className="text-sm">
            <strong>What's a Contact?</strong> A contact is any person you work with - could be a client, 
            a partner, or anyone important to your business relationships.
          </p>
        </div>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Plus className="w-5 h-5" />
            Real Example: Adding Your First Contact
          </CardTitle>
          <CardDescription>
            Let's add a contact together - follow along!
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="bg-blue-50 dark:bg-blue-950 p-4 rounded-lg">
            <p className="text-sm font-semibold mb-2">📝 Scenario: You just met Sarah Johnson from ABC Hotel Group</p>
            <p className="text-sm">Here's how to add her to the system:</p>
          </div>
          
          <ol className="space-y-4">
            <li className="flex items-start gap-3">
              <Badge className="bg-green-500">1</Badge>
              <div className="flex-1">
                <p className="font-semibold mb-2">Go to Contacts</p>
                <p className="text-sm text-muted-foreground mb-2">
                  Click "Contacts" in the left sidebar
                </p>
                <div className="bg-gray-50 dark:bg-gray-900 p-3 rounded text-xs">
                  You'll see a list of all your contacts. Don't worry if it's empty - we're about to fix that!
                </div>
              </div>
            </li>
            
            <li className="flex items-start gap-3">
              <Badge className="bg-green-500">2</Badge>
              <div className="flex-1">
                <p className="font-semibold mb-2">Click "Add Contact"</p>
                <p className="text-sm text-muted-foreground mb-2">
                  Look for the blue button that says "Add Contact" (usually top right)
                </p>
                <div className="bg-yellow-50 dark:bg-yellow-900 p-3 rounded text-xs">
                  <strong>Tip:</strong> Can't find it? Look for a "+" icon or "New" button
                </div>
              </div>
            </li>
            
            <li className="flex items-start gap-3">
              <Badge className="bg-green-500">3</Badge>
              <div className="flex-1">
                <p className="font-semibold mb-2">Fill in Sarah's Information</p>
                <div className="border rounded-lg p-3 space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="font-medium">First Name:</span>
                    <span className="text-muted-foreground">Sarah</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="font-medium">Last Name:</span>
                    <span className="text-muted-foreground">Johnson</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="font-medium">Email:</span>
                    <span className="text-muted-foreground">sarah.johnson@abchotel.com</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="font-medium">Job Title:</span>
                    <span className="text-muted-foreground">Marketing Director</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="font-medium">Organization:</span>
                    <span className="text-muted-foreground">ABC Hotel Group (select from dropdown)</span>
                  </div>
                </div>
              </div>
            </li>
            <li className="flex items-start gap-3">
              <span className="font-semibold text-primary">3.</span>
              <div>
                <p>Mark as "Primary Contact" if they're your main point of contact</p>
              </div>
            </li>
            <li className="flex items-start gap-3">
              <span className="font-semibold text-primary">4.</span>
              <div>
                <p>
                  Click <Badge>Save Contact</Badge> to create the contact
                </p>
              </div>
            </li>
          </ol>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Search className="w-5 h-5" />
            Finding Contacts
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            <div className="flex items-start gap-3">
              <Info className="w-5 h-5 text-blue-500 mt-1" />
              <div>
                <h4 className="font-semibold">Quick Search</h4>
                <p className="text-sm text-muted-foreground">
                  Use the search bar at the top of the Contacts page to search by name, email, or
                  organization.
                </p>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <Info className="w-5 h-5 text-blue-500 mt-1" />
              <div>
                <h4 className="font-semibold">Filters</h4>
                <p className="text-sm text-muted-foreground">
                  Filter contacts by organization or primary contact status using the dropdown
                  filters.
                </p>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Edit className="w-5 h-5" />
            Editing Contact Information
          </CardTitle>
        </CardHeader>
        <CardContent>
          <ol className="space-y-3">
            <li className="flex items-start gap-3">
              <span className="font-semibold text-primary">1.</span>
              <p>Click on any contact name to view their details</p>
            </li>
            <li className="flex items-start gap-3">
              <span className="font-semibold text-primary">2.</span>
              <p>
                Click the <Badge variant="outline">Edit</Badge> button in the top right
              </p>
            </li>
            <li className="flex items-start gap-3">
              <span className="font-semibold text-primary">3.</span>
              <p>Update any information as needed</p>
            </li>
            <li className="flex items-start gap-3">
              <span className="font-semibold text-primary">4.</span>
              <p>
                Click <Badge>Save Changes</Badge> to update the contact
              </p>
            </li>
          </ol>
        </CardContent>
      </Card>
    </div>
  );
}

// Organizations Content
function OrganizationsContent() {
  return (
    <div className="p-6 space-y-6">
      <div>
        <h2 className="text-2xl font-bold mb-4">Managing Organizations</h2>
        <p className="text-muted-foreground mb-6">
          Organizations represent your client companies. You can track their details, hierarchy, and
          relationships.
        </p>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Building2 className="w-5 h-5" />
            Organization Overview
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <h4 className="font-semibold">Organization Tiers</h4>
              <div className="space-y-1">
                <div className="flex items-center gap-2">
                  <Badge variant="default">Tier 1</Badge>
                  <span className="text-sm">Strategic Partners</span>
                </div>
                <div className="flex items-center gap-2">
                  <Badge variant="secondary">Tier 2</Badge>
                  <span className="text-sm">Key Accounts</span>
                </div>
                <div className="flex items-center gap-2">
                  <Badge variant="outline">Tier 3</Badge>
                  <span className="text-sm">Standard Clients</span>
                </div>
              </div>
            </div>
            <div className="space-y-2">
              <h4 className="font-semibold">Organization Status</h4>
              <div className="space-y-1">
                <div className="flex items-center gap-2">
                  <Badge className="bg-green-500">Active</Badge>
                  <span className="text-sm">Currently engaged</span>
                </div>
                <div className="flex items-center gap-2">
                  <Badge className="bg-yellow-500">Prospect</Badge>
                  <span className="text-sm">Potential client</span>
                </div>
                <div className="flex items-center gap-2">
                  <Badge className="bg-gray-500">Inactive</Badge>
                  <span className="text-sm">Past client</span>
                </div>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Layers className="w-5 h-5" />
            Organization Hierarchy
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            <p className="text-sm text-muted-foreground">
              Organizations can have parent-child relationships to represent corporate structures:
            </p>
            <div className="pl-4 space-y-2">
              <div className="flex items-center gap-2">
                <Building2 className="w-4 h-4" />
                <span className="font-semibold">Parent Company</span>
              </div>
              <div className="pl-6 space-y-2">
                <div className="flex items-center gap-2">
                  <ChevronRight className="w-4 h-4" />
                  <Building2 className="w-4 h-4" />
                  <span>Regional Office</span>
                </div>
                <div className="pl-6 flex items-center gap-2">
                  <ChevronRight className="w-4 h-4" />
                  <Building2 className="w-4 h-4" />
                  <span>Local Property</span>
                </div>
              </div>
            </div>
            <div className="flex items-start gap-2 mt-4">
              <Lightbulb className="w-4 h-4 text-yellow-500 mt-1" />
              <p className="text-sm text-muted-foreground">
                Use the Hierarchy tab on any organization page to view and manage relationships.
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Eye className="w-5 h-5" />
            Viewing Organization Details
          </CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-muted-foreground mb-4">
            Click on any organization to view comprehensive details across multiple tabs:
          </p>
          <div className="space-y-3">
            <div className="flex items-start gap-3">
              <Badge variant="outline">Details</Badge>
              <p className="text-sm">Basic information, contract details, and preferences</p>
            </div>
            <div className="flex items-start gap-3">
              <Badge variant="outline">Contacts</Badge>
              <p className="text-sm">All contacts associated with this organization</p>
            </div>
            <div className="flex items-start gap-3">
              <Badge variant="outline">Opportunities</Badge>
              <p className="text-sm">Sales opportunities and pipeline</p>
            </div>
            <div className="flex items-start gap-3">
              <Badge variant="outline">Projects</Badge>
              <p className="text-sm">Active and completed projects</p>
            </div>
            <div className="flex items-start gap-3">
              <Badge variant="outline">Activities</Badge>
              <p className="text-sm">Recent interactions and communications</p>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

// Opportunities Content
function OpportunitiesContent() {
  return (
    <div className="p-6 space-y-6">
      <div>
        <h2 className="text-2xl font-bold mb-4">Tracking Opportunities</h2>
        <p className="text-muted-foreground mb-6">
          Opportunities help you track potential deals through your sales pipeline from initial
          contact to closure.
        </p>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Workflow className="w-5 h-5" />
            Opportunity Stages
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            <p className="text-sm text-muted-foreground">
              Opportunities move through these stages:
            </p>
            <div className="flex items-center gap-2 flex-wrap">
              <Badge variant="outline">Lead</Badge>
              <ChevronRight className="w-4 h-4" />
              <Badge variant="outline">Qualified</Badge>
              <ChevronRight className="w-4 h-4" />
              <Badge variant="outline">Proposal</Badge>
              <ChevronRight className="w-4 h-4" />
              <Badge variant="outline">Negotiation</Badge>
              <ChevronRight className="w-4 h-4" />
              <Badge className="bg-green-500">Won</Badge>
              <span className="text-sm text-muted-foreground">or</span>
              <Badge className="bg-red-500">Lost</Badge>
            </div>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Plus className="w-5 h-5" />
            Creating an Opportunity
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <ol className="space-y-3">
            <li className="flex items-start gap-3">
              <span className="font-semibold text-primary">1.</span>
              <p>Navigate to the Opportunities page</p>
            </li>
            <li className="flex items-start gap-3">
              <span className="font-semibold text-primary">2.</span>
              <p>
                Click <Badge variant="secondary">New Opportunity</Badge>
              </p>
            </li>
            <li className="flex items-start gap-3">
              <span className="font-semibold text-primary">3.</span>
              <div>
                <p>Fill in the required fields:</p>
                <ul className="mt-2 ml-4 space-y-1 text-sm text-muted-foreground">
                  <li>• Opportunity Name</li>
                  <li>• Organization</li>
                  <li>• Primary Contact</li>
                  <li>• Value (estimated deal size)</li>
                  <li>• Expected Close Date</li>
                  <li>• Initial Stage</li>
                </ul>
              </div>
            </li>
            <li className="flex items-start gap-3">
              <span className="font-semibold text-primary">4.</span>
              <p>
                Click <Badge>Create Opportunity</Badge>
              </p>
            </li>
          </ol>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <BarChart3 className="w-5 h-5" />
            Tracking Progress
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            <div className="flex items-start gap-3">
              <CheckCircle className="w-5 h-5 text-green-500 mt-1" />
              <div>
                <h4 className="font-semibold">Update Stage</h4>
                <p className="text-sm text-muted-foreground">
                  As your opportunity progresses, update its stage to reflect the current status.
                </p>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <FileText className="w-5 h-5 text-blue-500 mt-1" />
              <div>
                <h4 className="font-semibold">Add Notes</h4>
                <p className="text-sm text-muted-foreground">
                  Document important conversations and decisions in the notes section.
                </p>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <Calendar className="w-5 h-5 text-purple-500 mt-1" />
              <div>
                <h4 className="font-semibold">Log Activities</h4>
                <p className="text-sm text-muted-foreground">
                  Record meetings, calls, and emails to maintain a complete history.
                </p>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

// Projects Content
function ProjectsContent() {
  return (
    <div className="p-6 space-y-6">
      <div>
        <h2 className="text-2xl font-bold mb-4">Project Management</h2>
        <p className="text-muted-foreground mb-6">
          Manage projects from inception to completion, tracking timelines, budgets, and team
          members.
        </p>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Briefcase className="w-5 h-5" />
            Project Lifecycle
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="space-y-2">
                <Badge className="bg-blue-500">Planning</Badge>
                <p className="text-sm text-muted-foreground">Define scope, budget, and timeline</p>
              </div>
              <div className="space-y-2">
                <Badge className="bg-yellow-500">In Progress</Badge>
                <p className="text-sm text-muted-foreground">Active development and execution</p>
              </div>
              <div className="space-y-2">
                <Badge className="bg-green-500">Completed</Badge>
                <p className="text-sm text-muted-foreground">Delivered and invoiced</p>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <DollarSign className="w-5 h-5" />
            Financial Tracking
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-3">
              <h4 className="font-semibold">Budget Management</h4>
              <ul className="space-y-2 text-sm text-muted-foreground">
                <li className="flex items-start gap-2">
                  <CheckCircle className="w-4 h-4 text-green-500 mt-0.5" />
                  <span>Set project budget and hourly rates</span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle className="w-4 h-4 text-green-500 mt-0.5" />
                  <span>Track time entries against budget</span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle className="w-4 h-4 text-green-500 mt-0.5" />
                  <span>Monitor profitability in real-time</span>
                </li>
              </ul>
            </div>
            <div className="space-y-3">
              <h4 className="font-semibold">Milestone Billing</h4>
              <ul className="space-y-2 text-sm text-muted-foreground">
                <li className="flex items-start gap-2">
                  <CheckCircle className="w-4 h-4 text-green-500 mt-0.5" />
                  <span>Create project milestones</span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle className="w-4 h-4 text-green-500 mt-0.5" />
                  <span>Generate invoices automatically</span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle className="w-4 h-4 text-green-500 mt-0.5" />
                  <span>Track milestone completion</span>
                </li>
              </ul>
            </div>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Users className="w-5 h-5" />
            Team Management
          </CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-muted-foreground mb-4">
            Assign team members and track their contributions:
          </p>
          <div className="space-y-3">
            <div className="flex items-start gap-3">
              <Badge variant="outline">1</Badge>
              <div>
                <p className="font-semibold">Add Team Members</p>
                <p className="text-sm text-muted-foreground">
                  Assign staff with allocation percentages
                </p>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <Badge variant="outline">2</Badge>
              <div>
                <p className="font-semibold">Track Time</p>
                <p className="text-sm text-muted-foreground">
                  Log hours worked with billable/non-billable designation
                </p>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <Badge variant="outline">3</Badge>
              <div>
                <p className="font-semibold">Monitor Costs</p>
                <p className="text-sm text-muted-foreground">See team costs vs. project revenue</p>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

// Land & Expand Content
function LandExpandContent() {
  return (
    <div className="p-6 space-y-6">
      <div>
        <h2 className="text-2xl font-bold mb-4">Land & Expand Strategy</h2>
        <p className="text-muted-foreground mb-6">
          Identify and track growth opportunities within your existing client base to maximize
          account value.
        </p>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <TrendingUp className="w-5 h-5" />
            Understanding Land & Expand
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="space-y-3">
                <div className="flex items-center gap-2">
                  <Target className="w-5 h-5 text-primary" />
                  <h4 className="font-semibold">Land</h4>
                </div>
                <p className="text-sm text-muted-foreground">
                  Initial engagement with a new client, typically starting with one service or
                  property.
                </p>
              </div>
              <div className="space-y-3">
                <div className="flex items-center gap-2">
                  <PieChart className="w-5 h-5 text-primary" />
                  <h4 className="font-semibold">Expand</h4>
                </div>
                <p className="text-sm text-muted-foreground">
                  Growing the relationship by adding more services, properties, or increasing
                  engagement.
                </p>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Eye className="w-5 h-5" />
            Viewing Growth Opportunities
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <p className="text-sm text-muted-foreground">
            The Land & Expand page shows a matrix view of your clients:
          </p>
          <div className="space-y-3">
            <div className="flex items-start gap-3">
              <Badge className="bg-green-500">High Growth</Badge>
              <p className="text-sm">Clients with significant expansion potential</p>
            </div>
            <div className="flex items-start gap-3">
              <Badge className="bg-yellow-500">Medium Growth</Badge>
              <p className="text-sm">Moderate expansion opportunities</p>
            </div>
            <div className="flex items-start gap-3">
              <Badge className="bg-blue-500">Maintain</Badge>
              <p className="text-sm">Focus on retention and satisfaction</p>
            </div>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Lightbulb className="w-5 h-5" />
            Expansion Strategies
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            <div className="space-y-3">
              <h4 className="font-semibold">Service Expansion</h4>
              <p className="text-sm text-muted-foreground">
                Look for clients using only one or two services who could benefit from your full
                portfolio.
              </p>
            </div>
            <div className="space-y-3">
              <h4 className="font-semibold">Geographic Expansion</h4>
              <p className="text-sm text-muted-foreground">
                Identify organizations with multiple properties where you're only working with one
                location.
              </p>
            </div>
            <div className="space-y-3">
              <h4 className="font-semibold">Vertical Integration</h4>
              <p className="text-sm text-muted-foreground">
                Move up the organizational hierarchy from property level to regional or corporate
                accounts.
              </p>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

// Gifts Content
function GiftsContent() {
  return (
    <div className="p-6 space-y-6">
      <div>
        <h2 className="text-2xl font-bold mb-4">Gift Tracking</h2>
        <p className="text-muted-foreground mb-6">
          Manage client gifts and track their impact on relationships through integrated surveys.
        </p>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Gift className="w-5 h-5" />
            Gift Management Process
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <ol className="space-y-3">
            <li className="flex items-start gap-3">
              <Badge>1</Badge>
              <div>
                <p className="font-semibold">Plan Gift Campaign</p>
                <p className="text-sm text-muted-foreground">
                  Select recipients and gift types for holidays or special occasions
                </p>
              </div>
            </li>
            <li className="flex items-start gap-3">
              <Badge>2</Badge>
              <div>
                <p className="font-semibold">Track Delivery</p>
                <p className="text-sm text-muted-foreground">
                  Monitor gift status from ordered to delivered
                </p>
              </div>
            </li>
            <li className="flex items-start gap-3">
              <Badge>3</Badge>
              <div>
                <p className="font-semibold">Collect Feedback</p>
                <p className="text-sm text-muted-foreground">
                  Automatic surveys sent to measure satisfaction
                </p>
              </div>
            </li>
            <li className="flex items-start gap-3">
              <Badge>4</Badge>
              <div>
                <p className="font-semibold">Analyze Impact</p>
                <p className="text-sm text-muted-foreground">
                  Review survey results and relationship improvements
                </p>
              </div>
            </li>
          </ol>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Mail className="w-5 h-5" />
            Survey Integration
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            <div className="flex items-start gap-3">
              <Info className="w-5 h-5 text-blue-500 mt-1" />
              <div>
                <h4 className="font-semibold">Automatic Surveys</h4>
                <p className="text-sm text-muted-foreground">
                  Google Forms surveys are automatically sent after gift delivery to gather
                  feedback.
                </p>
              </div>
            </div>
            <div className="space-y-2">
              <p className="text-sm font-semibold">Survey Questions Include:</p>
              <ul className="space-y-1 text-sm text-muted-foreground ml-4">
                <li>• Overall satisfaction rating (1-5 stars)</li>
                <li>• Gift appropriateness</li>
                <li>• Delivery experience</li>
                <li>• Would they recommend your services</li>
                <li>• Additional feedback</li>
              </ul>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

// Analytics Content
function AnalyticsContent() {
  return (
    <div className="p-6 space-y-6">
      <div>
        <h2 className="text-2xl font-bold mb-4">Analytics & Reports</h2>
        <p className="text-muted-foreground mb-6">
          Get insights into your business performance with comprehensive analytics and custom
          reports.
        </p>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <BarChart3 className="w-5 h-5" />
            Dashboard Analytics
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <p className="text-sm text-muted-foreground">
            Your dashboard provides real-time metrics:
          </p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <h4 className="font-semibold text-sm">Key Metrics</h4>
              <ul className="space-y-1 text-sm text-muted-foreground">
                <li>• Total Revenue</li>
                <li>• Active Projects</li>
                <li>• Pipeline Value</li>
                <li>• Client Count</li>
              </ul>
            </div>
            <div className="space-y-2">
              <h4 className="font-semibold text-sm">Visual Reports</h4>
              <ul className="space-y-1 text-sm text-muted-foreground">
                <li>• Revenue Trends</li>
                <li>• Client Distribution</li>
                <li>• Project Timeline</li>
                <li>• Team Utilization</li>
              </ul>
            </div>
          </div>
          <div className="flex items-start gap-2 mt-4">
            <Lightbulb className="w-4 h-4 text-yellow-500 mt-1" />
            <p className="text-sm text-muted-foreground">
              Customize your dashboard by dragging and dropping widgets to focus on what matters
              most.
            </p>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <FileText className="w-5 h-5" />
            Custom Reports
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <p className="text-sm text-muted-foreground">
            Generate detailed reports for deeper analysis:
          </p>
          <div className="space-y-3">
            <div className="flex items-start gap-3">
              <Badge variant="outline">Financial</Badge>
              <div>
                <p className="text-sm font-semibold">Revenue & Profitability</p>
                <p className="text-sm text-muted-foreground">
                  Project margins, client profitability, invoice aging
                </p>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <Badge variant="outline">Sales</Badge>
              <div>
                <p className="text-sm font-semibold">Pipeline Analysis</p>
                <p className="text-sm text-muted-foreground">
                  Conversion rates, sales velocity, win/loss analysis
                </p>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <Badge variant="outline">Client</Badge>
              <div>
                <p className="text-sm font-semibold">Relationship Health</p>
                <p className="text-sm text-muted-foreground">
                  Engagement scores, satisfaction trends, growth opportunities
                </p>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Download className="w-5 h-5" />
            Exporting Reports
          </CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-muted-foreground mb-4">
            Export your data in multiple formats:
          </p>
          <div className="flex gap-2 flex-wrap">
            <Badge variant="secondary">CSV</Badge>
            <Badge variant="secondary">Excel</Badge>
            <Badge variant="secondary">PDF</Badge>
            <Badge variant="secondary">JSON</Badge>
          </div>
          <p className="text-sm text-muted-foreground mt-4">
            Use the export button on any data table or report to download in your preferred format.
          </p>
        </CardContent>
      </Card>
    </div>
  );
}

// Import/Export Content
function ImportExportContent() {
  return (
    <div className="p-6 space-y-6">
      <div>
        <h2 className="text-2xl font-bold mb-4">Import & Export Data</h2>
        <p className="text-muted-foreground mb-6">
          Easily move data in and out of Spherical Manager with our AI-powered import tools.
        </p>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Upload className="w-5 h-5" />
            Importing Data
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-4">
            <div>
              <h4 className="font-semibold mb-2">AI-Powered Import Process</h4>
              <ol className="space-y-2 text-sm text-muted-foreground">
                <li className="flex items-start gap-2">
                  <span className="font-semibold">1.</span>
                  <span>Upload your CSV or Excel file</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="font-semibold">2.</span>
                  <span>AI automatically detects data types and suggests field mappings</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="font-semibold">3.</span>
                  <span>Review and adjust mappings if needed</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="font-semibold">4.</span>
                  <span>Preview data validation results</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="font-semibold">5.</span>
                  <span>Confirm import with automatic duplicate detection</span>
                </li>
              </ol>
            </div>
            <div className="flex items-start gap-2">
              <CheckCircle className="w-5 h-5 text-green-500 mt-1" />
              <div>
                <p className="text-sm font-semibold">Smart Matching</p>
                <p className="text-sm text-muted-foreground">
                  The AI learns from your data patterns to improve mapping suggestions over time.
                </p>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Download className="w-5 h-5" />
            Exporting Data
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <p className="text-sm text-muted-foreground">
            Export data from any section of the platform:
          </p>
          <div className="space-y-3">
            <div className="flex items-start gap-3">
              <Badge variant="outline">1</Badge>
              <div>
                <p className="font-semibold">Single Entity Export</p>
                <p className="text-sm text-muted-foreground">
                  Click the export button on any detail page to export that specific record
                </p>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <Badge variant="outline">2</Badge>
              <div>
                <p className="font-semibold">Bulk Export</p>
                <p className="text-sm text-muted-foreground">
                  Select multiple records and use the bulk actions menu to export
                </p>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <Badge variant="outline">3</Badge>
              <div>
                <p className="font-semibold">Full Data Export</p>
                <p className="text-sm text-muted-foreground">
                  Use the Data Export page to download complete datasets with relationships
                </p>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

// Integrations Content
function IntegrationsContent() {
  return (
    <div className="p-6 space-y-6">
      <div>
        <h2 className="text-2xl font-bold mb-4">External Integrations</h2>
        <p className="text-muted-foreground mb-6">
          Connect Spherical Manager with your other business tools for seamless workflows.
        </p>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Link className="w-5 h-5" />
            Available Integrations
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-3">
              <div className="flex items-start gap-3">
                <div className="w-8 h-8 bg-orange-100 rounded flex items-center justify-center">
                  <span className="text-xs font-bold text-orange-600">HS</span>
                </div>
                <div>
                  <h4 className="font-semibold">HubSpot</h4>
                  <p className="text-sm text-muted-foreground">
                    Bi-directional sync of contacts, companies, and deals
                  </p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <div className="w-8 h-8 bg-purple-100 rounded flex items-center justify-center">
                  <span className="text-xs font-bold text-purple-600">S</span>
                </div>
                <div>
                  <h4 className="font-semibold">Stripe</h4>
                  <p className="text-sm text-muted-foreground">Payment processing and invoicing</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <div className="w-8 h-8 bg-green-100 rounded flex items-center justify-center">
                  <span className="text-xs font-bold text-green-600">QB</span>
                </div>
                <div>
                  <h4 className="font-semibold">QuickBooks</h4>
                  <p className="text-sm text-muted-foreground">Accounting synchronization</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <div className="w-8 h-8 bg-red-100 rounded flex items-center justify-center">
                  <span className="text-xs font-bold text-red-600">G</span>
                </div>
                <div>
                  <h4 className="font-semibold">Gmail</h4>
                  <p className="text-sm text-muted-foreground">Email and calendar sync</p>
                </div>
              </div>
            </div>
            <div className="space-y-3">
              <div className="flex items-start gap-3">
                <div className="w-8 h-8 bg-blue-100 rounded flex items-center justify-center">
                  <span className="text-xs font-bold text-blue-600">O</span>
                </div>
                <div>
                  <h4 className="font-semibold">Outlook</h4>
                  <p className="text-sm text-muted-foreground">Microsoft 365 integration</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <div className="w-8 h-8 bg-yellow-100 rounded flex items-center justify-center">
                  <span className="text-xs font-bold text-yellow-600">H</span>
                </div>
                <div>
                  <h4 className="font-semibold">HIVE</h4>
                  <p className="text-sm text-muted-foreground">Project management sync</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <div className="w-8 h-8 bg-green-100 rounded flex items-center justify-center">
                  <span className="text-xs font-bold text-green-600">E</span>
                </div>
                <div>
                  <h4 className="font-semibold">Expensify</h4>
                  <p className="text-sm text-muted-foreground">Expense tracking</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <div className="w-8 h-8 bg-indigo-100 rounded flex items-center justify-center">
                  <span className="text-xs font-bold text-indigo-600">T</span>
                </div>
                <div>
                  <h4 className="font-semibold">Twilio</h4>
                  <p className="text-sm text-muted-foreground">SMS and voice communication</p>
                </div>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Settings className="w-5 h-5" />
            Setting Up Integrations
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <p className="text-sm text-muted-foreground">
            HubSpot integration can be configured by administrators:
          </p>
          <ol className="space-y-3">
            <li className="flex items-start gap-3">
              <Badge>1</Badge>
              <p className="text-sm">Navigate to Integrations → HubSpot in the sidebar</p>
            </li>
            <li className="flex items-start gap-3">
              <Badge>2</Badge>
              <p className="text-sm">Click "Setup HubSpot Integration" to begin configuration</p>
            </li>
            <li className="flex items-start gap-3">
              <Badge>3</Badge>
              <p className="text-sm">
                Follow the setup wizard to authenticate with your HubSpot account
              </p>
            </li>
            <li className="flex items-start gap-3">
              <Badge>4</Badge>
              <p className="text-sm">Configure field mappings between systems</p>
            </li>
            <li className="flex items-start gap-3">
              <Badge>5</Badge>
              <p className="text-sm">Test the connection and start syncing data</p>
            </li>
            <li className="flex items-start gap-3">
              <Badge>6</Badge>
              <p className="text-sm">Monitor sync status and logs for any issues</p>
            </li>
          </ol>
          <div className="flex items-start gap-2 mt-4">
            <Shield className="w-4 h-4 text-green-500 mt-1" />
            <p className="text-sm text-muted-foreground">
              All integrations use secure OAuth authentication and encrypted data transfer.
            </p>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

// Best Practices Content
function BestPracticesContent() {
  return (
    <div className="p-6 space-y-6">
      <div>
        <h2 className="text-2xl font-bold mb-4">Best Practices</h2>
        <p className="text-muted-foreground mb-6">
          Follow these guidelines to get the most out of Spherical Manager.
        </p>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Users className="w-5 h-5" />
            Data Management
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-3">
            <div className="flex items-start gap-3">
              <CheckCircle className="w-5 h-5 text-green-500 mt-1" />
              <div>
                <h4 className="font-semibold">Keep Contact Information Current</h4>
                <p className="text-sm text-muted-foreground">
                  Regularly update contact details, especially email addresses and phone numbers.
                </p>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <CheckCircle className="w-5 h-5 text-green-500 mt-1" />
              <div>
                <h4 className="font-semibold">Use Consistent Naming</h4>
                <p className="text-sm text-muted-foreground">
                  Establish naming conventions for organizations and maintain them across the team.
                </p>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <CheckCircle className="w-5 h-5 text-green-500 mt-1" />
              <div>
                <h4 className="font-semibold">Complete All Required Fields</h4>
                <p className="text-sm text-muted-foreground">
                  The more complete your data, the better the analytics and insights.
                </p>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Workflow className="w-5 h-5" />
            Workflow Tips
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-3">
            <div className="flex items-start gap-3">
              <Lightbulb className="w-5 h-5 text-yellow-500 mt-1" />
              <div>
                <h4 className="font-semibold">Log Activities Immediately</h4>
                <p className="text-sm text-muted-foreground">
                  Record meetings, calls, and important emails right after they happen while details
                  are fresh.
                </p>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <Lightbulb className="w-5 h-5 text-yellow-500 mt-1" />
              <div>
                <h4 className="font-semibold">Update Opportunity Stages</h4>
                <p className="text-sm text-muted-foreground">
                  Keep your pipeline accurate by updating stages as deals progress.
                </p>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <Lightbulb className="w-5 h-5 text-yellow-500 mt-1" />
              <div>
                <h4 className="font-semibold">Use Tags and Categories</h4>
                <p className="text-sm text-muted-foreground">
                  Categorize contacts and organizations for easier filtering and reporting.
                </p>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Shield className="w-5 h-5" />
            Security Best Practices
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-3">
            <div className="flex items-start gap-3">
              <AlertCircle className="w-5 h-5 text-red-500 mt-1" />
              <div>
                <h4 className="font-semibold">Protect Your Login</h4>
                <p className="text-sm text-muted-foreground">
                  Use a strong, unique password and never share your credentials.
                </p>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <AlertCircle className="w-5 h-5 text-red-500 mt-1" />
              <div>
                <h4 className="font-semibold">Log Out When Done</h4>
                <p className="text-sm text-muted-foreground">
                  Always log out when using shared computers or finishing your work session.
                </p>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <AlertCircle className="w-5 h-5 text-red-500 mt-1" />
              <div>
                <h4 className="font-semibold">Report Suspicious Activity</h4>
                <p className="text-sm text-muted-foreground">
                  Contact your administrator immediately if you notice any unusual activity.
                </p>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

// Invoices Content
function InvoicesContent() {
  return (
    <div className="p-6 space-y-6">
      <div>
        <h2 className="text-2xl font-bold mb-4">Invoice Management</h2>
        <p className="text-muted-foreground mb-6">
          Create, track, and manage invoices for your projects and services with automatic milestone
          invoicing.
        </p>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Plus className="w-5 h-5" />
            Creating Invoices
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <p className="text-sm text-muted-foreground mb-4">
            Invoices can be created manually or automatically from project milestones:
          </p>
          <div className="space-y-3">
            <div className="flex items-start gap-3">
              <Badge>1</Badge>
              <div>
                <p className="font-semibold">Manual Invoice Creation</p>
                <p className="text-sm text-muted-foreground">
                  Navigate to Invoices → Create Invoice to build a custom invoice
                </p>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <Badge>2</Badge>
              <div>
                <p className="font-semibold">Milestone-Based Invoicing</p>
                <p className="text-sm text-muted-foreground">
                  Invoices are automatically generated when project milestones are completed
                </p>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <Badge>3</Badge>
              <div>
                <p className="font-semibold">Template-Based Invoicing</p>
                <p className="text-sm text-muted-foreground">
                  Use predefined templates for consistent invoice formatting
                </p>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <BarChart3 className="w-5 h-5" />
            Invoice Status Tracking
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            <p className="text-sm text-muted-foreground">
              Track invoice progress through these stages:
            </p>
            <div className="flex items-center gap-2 flex-wrap">
              <Badge variant="outline">Draft</Badge>
              <ChevronRight className="w-4 h-4" />
              <Badge variant="outline">Sent</Badge>
              <ChevronRight className="w-4 h-4" />
              <Badge variant="outline">Viewed</Badge>
              <ChevronRight className="w-4 h-4" />
              <Badge className="bg-green-500">Paid</Badge>
              <span className="text-sm text-muted-foreground">or</span>
              <Badge className="bg-red-500">Overdue</Badge>
            </div>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Download className="w-5 h-5" />
            PDF Generation & Delivery
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            <div className="flex items-start gap-3">
              <CheckCircle className="w-5 h-5 text-green-500 mt-1" />
              <div>
                <p className="font-semibold">Automatic PDF Creation</p>
                <p className="text-sm text-muted-foreground">
                  Professional PDFs are generated using jsPDF with your invoice details and line items
                </p>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <CheckCircle className="w-5 h-5 text-green-500 mt-1" />
              <div>
                <p className="font-semibold">Email Delivery</p>
                <p className="text-sm text-muted-foreground">
                  Send invoices directly to clients via email with PDF attachments
                </p>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <CheckCircle className="w-5 h-5 text-green-500 mt-1" />
              <div>
                <p className="font-semibold">Payment Tracking</p>
                <p className="text-sm text-muted-foreground">
                  Monitor invoice status and send automated reminders
                </p>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

// Documents Content
function DocumentsContent() {
  return (
    <div className="p-6 space-y-6">
      <div>
        <h2 className="text-2xl font-bold mb-4">Document Management</h2>
        <p className="text-muted-foreground mb-6">
          Store, organize, and share documents with version control and permission management.
        </p>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Upload className="w-5 h-5" />
            Uploading Documents
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <ol className="space-y-3">
            <li className="flex items-start gap-3">
              <Badge>1</Badge>
              <p>Navigate to Documents → Upload Document</p>
            </li>
            <li className="flex items-start gap-3">
              <Badge>2</Badge>
              <p>Select file(s) or drag and drop onto the upload area</p>
            </li>
            <li className="flex items-start gap-3">
              <Badge>3</Badge>
              <div>
                <p>Add document metadata:</p>
                <ul className="mt-2 ml-4 space-y-1 text-sm text-muted-foreground">
                  <li>• Document title and description</li>
                  <li>• Category and tags</li>
                  <li>• Related organization or project</li>
                  <li>• Access permissions</li>
                </ul>
              </div>
            </li>
            <li className="flex items-start gap-3">
              <Badge>4</Badge>
              <p>Click Upload to save the document</p>
            </li>
          </ol>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Layers className="w-5 h-5" />
            Version Control
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            <p className="text-sm text-muted-foreground">
              Documents support automatic version control:
            </p>
            <div className="space-y-3">
              <div className="flex items-start gap-3">
                <CheckCircle className="w-5 h-5 text-green-500 mt-1" />
                <div>
                  <p className="font-semibold">Automatic Versioning</p>
                  <p className="text-sm text-muted-foreground">
                    New versions are created when documents are updated
                  </p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <CheckCircle className="w-5 h-5 text-green-500 mt-1" />
                <div>
                  <p className="font-semibold">Version History</p>
                  <p className="text-sm text-muted-foreground">
                    View complete history with timestamps and change notes
                  </p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <CheckCircle className="w-5 h-5 text-green-500 mt-1" />
                <div>
                  <p className="font-semibold">Version Restoration</p>
                  <p className="text-sm text-muted-foreground">
                    Restore previous versions if needed
                  </p>
                </div>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Shield className="w-5 h-5" />
            Document Permissions
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            <p className="text-sm text-muted-foreground">
              Control who can access and modify documents:
            </p>
            <div className="space-y-2">
              <div className="flex items-center gap-2">
                <Badge variant="outline">View</Badge>
                <span className="text-sm">Can read and download the document</span>
              </div>
              <div className="flex items-center gap-2">
                <Badge variant="outline">Edit</Badge>
                <span className="text-sm">Can modify and update the document</span>
              </div>
              <div className="flex items-center gap-2">
                <Badge variant="outline">Admin</Badge>
                <span className="text-sm">Can manage permissions and delete</span>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

// User Management Content
function UserManagementContent() {
  return (
    <div className="p-6 space-y-6">
      <div>
        <h2 className="text-2xl font-bold mb-4">User Management</h2>
        <p className="text-muted-foreground mb-6">
          Manage user accounts, roles, and permissions. Available to administrators only.
        </p>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <AlertCircle className="w-5 h-5 text-blue-500" />
            Administrator Access Required
          </CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-muted-foreground">
            User management features are only available to users with Administrator role. If you
            need access to these features, contact your system administrator.
          </p>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <UserPlus className="w-5 h-5" />
            Inviting New Users
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <ol className="space-y-3">
            <li className="flex items-start gap-3">
              <Badge>1</Badge>
              <p>Navigate to Admin → User Management</p>
            </li>
            <li className="flex items-start gap-3">
              <Badge>2</Badge>
              <p>Click "Invite User" button</p>
            </li>
            <li className="flex items-start gap-3">
              <Badge>3</Badge>
              <div>
                <p>Fill in user details:</p>
                <ul className="mt-2 ml-4 space-y-1 text-sm text-muted-foreground">
                  <li>• Email address</li>
                  <li>• First and last name</li>
                  <li>• Role assignment</li>
                  <li>• Department (optional)</li>
                </ul>
              </div>
            </li>
            <li className="flex items-start gap-3">
              <Badge>4</Badge>
              <p>Send invitation email with login instructions</p>
            </li>
          </ol>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Shield className="w-5 h-5" />
            Role Management
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            <p className="text-sm text-muted-foreground">
              Assign roles to control user access and permissions:
            </p>
            <div className="space-y-3">
              <div className="flex items-start gap-3">
                <Badge className="bg-red-500">Admin</Badge>
                <div>
                  <p className="font-semibold">Administrator</p>
                  <p className="text-sm text-muted-foreground">
                    Full access to all features including user management
                  </p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <Badge className="bg-blue-500">Manager</Badge>
                <div>
                  <p className="font-semibold">Manager</p>
                  <p className="text-sm text-muted-foreground">
                    Access to all client data and reporting features
                  </p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <Badge className="bg-green-500">User</Badge>
                <div>
                  <p className="font-semibold">Standard User</p>
                  <p className="text-sm text-muted-foreground">
                    Access to assigned accounts and basic functionality
                  </p>
                </div>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

// Settings Content
function SettingsContent() {
  return (
    <div className="p-6 space-y-6">
      <div>
        <h2 className="text-2xl font-bold mb-4">Settings & Preferences</h2>
        <p className="text-muted-foreground mb-6">
          Customize your experience and manage your account preferences.
        </p>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Settings className="w-5 h-5" />
            Accessing Settings
          </CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-muted-foreground mb-4">Access your settings in two ways:</p>
          <div className="space-y-2">
            <div className="flex items-center gap-2">
              <Badge variant="outline">1</Badge>
              <span className="text-sm">Click Settings in the sidebar navigation</span>
            </div>
            <div className="flex items-center gap-2">
              <Badge variant="outline">2</Badge>
              <span className="text-sm">Use your profile dropdown in the top right</span>
            </div>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Users className="w-5 h-5" />
            Account Settings
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <p className="text-sm text-muted-foreground">Manage your basic account information:</p>
          <div className="space-y-3">
            <div className="flex items-start gap-3">
              <Info className="w-5 h-5 text-blue-500 mt-1" />
              <div>
                <p className="font-semibold">Profile Information</p>
                <p className="text-sm text-muted-foreground">
                  Update your name and view your email address and role assignment
                </p>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <Info className="w-5 h-5 text-blue-500 mt-1" />
              <div>
                <p className="font-semibold">Password Management</p>
                <p className="text-sm text-muted-foreground">
                  Change your password anytime for enhanced security
                </p>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <Info className="w-5 h-5 text-blue-500 mt-1" />
              <div>
                <p className="font-semibold">Timezone Settings</p>
                <p className="text-sm text-muted-foreground">
                  Your timezone is automatically detected for accurate timestamps
                </p>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Mail className="w-5 h-5" />
            Notification Preferences
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <p className="text-sm text-muted-foreground mb-4">
            Control how you receive notifications:
          </p>
          <div className="space-y-3">
            <div className="flex items-center justify-between p-3 border rounded">
              <div>
                <p className="font-semibold">Email Notifications</p>
                <p className="text-sm text-muted-foreground">Receive important updates via email</p>
              </div>
              <Badge variant="outline">Configurable</Badge>
            </div>
            <div className="flex items-center justify-between p-3 border rounded">
              <div>
                <p className="font-semibold">Weekly Reports</p>
                <p className="text-sm text-muted-foreground">Get weekly summary emails</p>
              </div>
              <Badge variant="outline">Configurable</Badge>
            </div>
            <div className="flex items-center justify-between p-3 border rounded">
              <div>
                <p className="font-semibold">Push Notifications</p>
                <p className="text-sm text-muted-foreground">
                  Browser notifications for urgent items
                </p>
              </div>
              <Badge variant="outline">Configurable</Badge>
            </div>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Eye className="w-5 h-5" />
            Application Preferences
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-3">
            <div className="flex items-center justify-between p-3 border rounded">
              <div>
                <p className="font-semibold">Auto Refresh</p>
                <p className="text-sm text-muted-foreground">
                  Automatically refresh data every 5 minutes
                </p>
              </div>
              <Badge variant="outline">Enabled by Default</Badge>
            </div>
            <div className="flex items-center justify-between p-3 border rounded">
              <div>
                <p className="font-semibold">Dark Mode</p>
                <p className="text-sm text-muted-foreground">Switch between light and dark themes</p>
              </div>
              <Badge variant="outline">Available</Badge>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

// Troubleshooting Content
function TroubleshootingContent() {
  return (
    <div className="p-6 space-y-6">
      <div>
        <h2 className="text-2xl font-bold mb-4">Troubleshooting</h2>
        <p className="text-muted-foreground mb-6">
          Solutions to common issues and how to get help when you need it.
        </p>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <HelpCircle className="w-5 h-5" />
            Common Issues
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-4">
            <div className="space-y-2">
              <h4 className="font-semibold">Can't Log In</h4>
              <ul className="space-y-1 text-sm text-muted-foreground ml-4">
                <li>• Check your email address for typos</li>
                <li>• Ensure Caps Lock is off</li>
                <li>• Try resetting your password</li>
                <li>• Contact your administrator if issues persist</li>
              </ul>
            </div>
            <Separator />
            <div className="space-y-2">
              <h4 className="font-semibold">Data Not Showing</h4>
              <ul className="space-y-1 text-sm text-muted-foreground ml-4">
                <li>• Refresh the page (Ctrl/Cmd + R)</li>
                <li>• Check your filters - you may have active filters hiding data</li>
                <li>• Verify you have permission to view the data</li>
                <li>• Try logging out and back in</li>
              </ul>
            </div>
            <Separator />
            <div className="space-y-2">
              <h4 className="font-semibold">Slow Performance</h4>
              <ul className="space-y-1 text-sm text-muted-foreground ml-4">
                <li>• Check your internet connection</li>
                <li>• Clear your browser cache</li>
                <li>• Try using a different browser</li>
                <li>• Close unnecessary browser tabs</li>
              </ul>
            </div>
            <Separator />
            <div className="space-y-2">
              <h4 className="font-semibold">Import Errors</h4>
              <ul className="space-y-1 text-sm text-muted-foreground ml-4">
                <li>• Ensure your file is in CSV or Excel format</li>
                <li>• Check for special characters in your data</li>
                <li>• Verify required fields are populated</li>
                <li>• Review the error message for specific issues</li>
              </ul>
            </div>
            <Separator />
            <div className="space-y-2">
              <h4 className="font-semibold">Database or API Errors (400/404)</h4>
              <ul className="space-y-1 text-sm text-muted-foreground ml-4">
                <li>• Contact your administrator to run database migrations</li>
                <li>• Clear browser cache and refresh the page</li>
                <li>• Check browser console for specific error messages</li>
                <li>• Ensure you're using the latest version of the application</li>
              </ul>
            </div>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Info className="w-5 h-5" />
            Getting Help
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-3">
            <div className="flex items-start gap-3">
              <Badge>1</Badge>
              <div>
                <h4 className="font-semibold">Check Documentation</h4>
                <p className="text-sm text-muted-foreground">
                  This guide covers most common tasks and issues
                </p>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <Badge>2</Badge>
              <div>
                <h4 className="font-semibold">Ask Your Team</h4>
                <p className="text-sm text-muted-foreground">
                  Colleagues may have encountered similar issues
                </p>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <Badge>3</Badge>
              <div>
                <h4 className="font-semibold">Contact Support</h4>
                <p className="text-sm text-muted-foreground">
                  Reach out to your system administrator or Spherical support team
                </p>
              </div>
            </div>
          </div>
          <div className="mt-6 p-4 bg-muted rounded-lg">
            <p className="text-sm font-semibold mb-2">Support Contact:</p>
            <p className="text-sm text-muted-foreground">Email: support@sphericalmanager.com</p>
            <p className="text-sm text-muted-foreground">
              Include screenshots and detailed descriptions of issues
            </p>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

// Automation Content
function AutomationContent() {
  return (
    <div className="p-6 space-y-6">
      <div>
        <h2 className="text-2xl font-bold mb-4">Workflow Automation</h2>
        <p className="text-muted-foreground mb-6">
          Automate repetitive tasks and create powerful workflows to streamline your business
          processes.
        </p>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Workflow className="w-5 h-5" />
            Understanding Workflow Automation
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <p className="text-sm text-muted-foreground">
            Workflow automation allows you to create rules that trigger actions automatically based
            on specific events.
          </p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <h4 className="font-semibold">Common Triggers</h4>
              <ul className="space-y-1 text-sm text-muted-foreground">
                <li>• New contact created</li>
                <li>• Opportunity stage changed</li>
                <li>• Task overdue</li>
                <li>• Invoice paid</li>
                <li>• Project completed</li>
              </ul>
            </div>
            <div className="space-y-2">
              <h4 className="font-semibold">Available Actions</h4>
              <ul className="space-y-1 text-sm text-muted-foreground">
                <li>• Send email notification</li>
                <li>• Create follow-up task</li>
                <li>• Update record fields</li>
                <li>• Assign to team member</li>
                <li>• Trigger webhook</li>
              </ul>
            </div>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Plus className="w-5 h-5" />
            Creating a Workflow
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <ol className="space-y-3">
            <li className="flex items-start gap-3">
              <Badge>1</Badge>
              <div>
                <p className="font-semibold">Navigate to Automation</p>
                <p className="text-sm text-muted-foreground">
                  Access the Automation section from the sidebar
                </p>
              </div>
            </li>
            <li className="flex items-start gap-3">
              <Badge>2</Badge>
              <div>
                <p className="font-semibold">Create New Workflow</p>
                <p className="text-sm text-muted-foreground">
                  Click "Create Workflow" and give it a descriptive name
                </p>
              </div>
            </li>
            <li className="flex items-start gap-3">
              <Badge>3</Badge>
              <div>
                <p className="font-semibold">Set Trigger</p>
                <p className="text-sm text-muted-foreground">
                  Choose what event will trigger this workflow
                </p>
              </div>
            </li>
            <li className="flex items-start gap-3">
              <Badge>4</Badge>
              <div>
                <p className="font-semibold">Add Conditions (Optional)</p>
                <p className="text-sm text-muted-foreground">
                  Set filters to only run the workflow under specific conditions
                </p>
              </div>
            </li>
            <li className="flex items-start gap-3">
              <Badge>5</Badge>
              <div>
                <p className="font-semibold">Configure Actions</p>
                <p className="text-sm text-muted-foreground">
                  Add one or more actions to execute when triggered
                </p>
              </div>
            </li>
            <li className="flex items-start gap-3">
              <Badge>6</Badge>
              <div>
                <p className="font-semibold">Test & Activate</p>
                <p className="text-sm text-muted-foreground">
                  Test with sample data, then activate the workflow
                </p>
              </div>
            </li>
          </ol>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Lightbulb className="w-5 h-5" />
            Workflow Examples
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-4">
            <div className="p-4 border rounded-lg">
              <h4 className="font-semibold mb-2">Lead Assignment</h4>
              <p className="text-sm text-muted-foreground mb-2">
                Automatically assign new leads based on territory or expertise
              </p>
              <div className="text-sm">
                <Badge variant="outline">Trigger</Badge> New contact created →
                <Badge variant="outline">Condition</Badge> Industry = Hospitality →
                <Badge variant="outline">Action</Badge> Assign to hospitality team
              </div>
            </div>
            <div className="p-4 border rounded-lg">
              <h4 className="font-semibold mb-2">Follow-up Reminders</h4>
              <p className="text-sm text-muted-foreground mb-2">
                Create tasks for follow-ups after meetings
              </p>
              <div className="text-sm">
                <Badge variant="outline">Trigger</Badge> Activity completed →
                <Badge variant="outline">Condition</Badge> Type = Meeting →
                <Badge variant="outline">Action</Badge> Create task in 3 days
              </div>
            </div>
            <div className="p-4 border rounded-lg">
              <h4 className="font-semibold mb-2">Contract Renewal</h4>
              <p className="text-sm text-muted-foreground mb-2">
                Notify account managers of upcoming renewals
              </p>
              <div className="text-sm">
                <Badge variant="outline">Trigger</Badge> Date approaching →
                <Badge variant="outline">Condition</Badge> Contract end - 60 days →
                <Badge variant="outline">Action</Badge> Email notification + Create opportunity
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Layers className="w-5 h-5" />
            n8n Integration Templates
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <p className="text-sm text-muted-foreground">
            Spherical Manager includes pre-built n8n workflow templates for advanced automation:
          </p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-3">
              <h4 className="font-semibold">Available Templates</h4>
              <ul className="space-y-2 text-sm text-muted-foreground">
                <li>• Client Onboarding Automation</li>
                <li>• Invoice Payment Follow-up</li>
                <li>• Client Health Monitoring</li>
                <li>• Hotel Chain Onboarding</li>
                <li>• Land & Expand Opportunity Scanner</li>
              </ul>
            </div>
            <div className="space-y-3">
              <h4 className="font-semibold">More Templates</h4>
              <ul className="space-y-2 text-sm text-muted-foreground">
                <li>• SEO Performance Reporting</li>
                <li>• Contract Renewal Automation</li>
                <li>• Project Profitability Alerts</li>
                <li>• Client Satisfaction Workflow</li>
                <li>• Service Expansion Analyzer</li>
              </ul>
            </div>
          </div>
          <div className="mt-4 p-4 bg-muted/30 rounded-lg">
            <p className="text-sm font-medium mb-2">Using n8n Templates:</p>
            <ol className="space-y-1 text-sm text-muted-foreground">
              <li>1. Go to Automation → n8n Templates tab</li>
              <li>2. Browse and download template JSON files</li>
              <li>3. Import into your n8n instance</li>
              <li>4. Configure webhook URLs and API credentials</li>
              <li>5. Test in development mode before activating</li>
            </ol>
          </div>
          <div className="flex items-start gap-2 p-3 bg-yellow-50 dark:bg-yellow-900/20 rounded-lg">
            <AlertCircle className="w-4 h-4 text-yellow-600 dark:text-yellow-400 mt-0.5" />
            <div className="text-sm">
              <p className="font-medium text-yellow-900 dark:text-yellow-100">
                Test Mode Available
              </p>
              <p className="text-yellow-700 dark:text-yellow-300">
                All workflows include a test mode to prevent accidental client communications during
                setup.
              </p>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

// Communication Hub Content
function CommunicationHubContent() {
  return (
    <div className="p-6 space-y-6">
      <div>
        <h2 className="text-2xl font-bold mb-4">Communication Hub</h2>
        <p className="text-muted-foreground mb-6">
          Centralize all your communications with email integration, calendar sync, and unified
          messaging.
        </p>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Mail className="w-5 h-5" />
            Email Integration
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <p className="text-sm text-muted-foreground mb-4">
            Connect your email accounts for seamless communication tracking:
          </p>
          <div className="space-y-3">
            <div className="flex items-start gap-3">
              <div className="w-8 h-8 bg-blue-100 rounded flex items-center justify-center">
                <Mail className="w-4 h-4 text-blue-600" />
              </div>
              <div>
                <h4 className="font-semibold">Gmail Integration</h4>
                <p className="text-sm text-muted-foreground">
                  Connect Google Workspace accounts with full sync
                </p>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <div className="w-8 h-8 bg-blue-100 rounded flex items-center justify-center">
                <Mail className="w-4 h-4 text-blue-600" />
              </div>
              <div>
                <h4 className="font-semibold">Outlook Integration</h4>
                <p className="text-sm text-muted-foreground">
                  Microsoft 365 email and calendar synchronization
                </p>
              </div>
            </div>
          </div>
          <div className="flex items-start gap-2 mt-4">
            <Info className="w-4 h-4 text-blue-500 mt-1" />
            <p className="text-sm text-muted-foreground">
              Emails are automatically linked to the relevant contacts and organizations.
            </p>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Calendar className="w-5 h-5" />
            Calendar Synchronization
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <p className="text-sm text-muted-foreground">
            Keep your calendar in sync with CRM activities:
          </p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <h4 className="font-semibold text-sm">Sync From Calendar</h4>
              <ul className="space-y-1 text-sm text-muted-foreground">
                <li>• Client meetings</li>
                <li>• Call appointments</li>
                <li>• Event reminders</li>
                <li>• Team meetings</li>
              </ul>
            </div>
            <div className="space-y-2">
              <h4 className="font-semibold text-sm">Sync To Calendar</h4>
              <ul className="space-y-1 text-sm text-muted-foreground">
                <li>• Task due dates</li>
                <li>• Follow-up reminders</li>
                <li>• Project milestones</li>
                <li>• Contract renewals</li>
              </ul>
            </div>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <FileText className="w-5 h-5" />
            Email Templates
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <p className="text-sm text-muted-foreground">
            Create and manage email templates for consistent communication:
          </p>
          <ol className="space-y-3">
            <li className="flex items-start gap-3">
              <Badge variant="outline">1</Badge>
              <p className="text-sm">Navigate to Communication Hub → Email Templates</p>
            </li>
            <li className="flex items-start gap-3">
              <Badge variant="outline">2</Badge>
              <p className="text-sm">Click "Create Template" to start a new template</p>
            </li>
            <li className="flex items-start gap-3">
              <Badge variant="outline">3</Badge>
              <p className="text-sm">
                Use merge fields like {`{{contact.first_name}}`} for personalization
              </p>
            </li>
            <li className="flex items-start gap-3">
              <Badge variant="outline">4</Badge>
              <p className="text-sm">Save and use templates when composing emails</p>
            </li>
          </ol>
          <div className="mt-4 p-4 bg-muted rounded-lg">
            <p className="text-sm font-semibold mb-2">Popular Templates:</p>
            <ul className="space-y-1 text-sm text-muted-foreground">
              <li>• Welcome email for new clients</li>
              <li>• Meeting follow-up template</li>
              <li>• Proposal submission</li>
              <li>• Thank you notes</li>
              <li>• Contract renewal reminder</li>
            </ul>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <BarChart3 className="w-5 h-5" />
            Communication Analytics
          </CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-muted-foreground mb-4">
            Track your communication effectiveness:
          </p>
          <div className="grid grid-cols-2 gap-4">
            <div className="text-center p-3 border rounded">
              <p className="text-2xl font-bold text-primary">85%</p>
              <p className="text-sm text-muted-foreground">Email Open Rate</p>
            </div>
            <div className="text-center p-3 border rounded">
              <p className="text-2xl font-bold text-primary">32%</p>
              <p className="text-sm text-muted-foreground">Response Rate</p>
            </div>
            <div className="text-center p-3 border rounded">
              <p className="text-2xl font-bold text-primary">2.5h</p>
              <p className="text-sm text-muted-foreground">Avg Response Time</p>
            </div>
            <div className="text-center p-3 border rounded">
              <p className="text-2xl font-bold text-primary">156</p>
              <p className="text-sm text-muted-foreground">Emails This Week</p>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

// Financial Management Content
function FinancialContent() {
  return (
    <div className="p-6 space-y-6">
      <div>
        <h2 className="text-2xl font-bold mb-4">Financial Management</h2>
        <p className="text-muted-foreground mb-6">
          Comprehensive financial tools including invoice management, accounting integration, and
          financial analytics.
        </p>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <DollarSign className="w-5 h-5" />
            Invoice Management
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <p className="text-sm text-muted-foreground">
            Comprehensive invoice tracking and management system:
          </p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="p-4 border rounded-lg">
              <h4 className="font-semibold mb-2">Invoice Features</h4>
              <ul className="space-y-1 text-sm text-muted-foreground">
                <li>• Professional invoice generation</li>
                <li>• Customizable templates</li>
                <li>• Automated numbering</li>
                <li>• Multi-currency support</li>
              </ul>
            </div>
            <div className="p-4 border rounded-lg">
              <h4 className="font-semibold mb-2">Tracking Features</h4>
              <ul className="space-y-1 text-sm text-muted-foreground">
                <li>• Invoice status tracking</li>
                <li>• Manual invoice status tracking</li>
                <li>• Balance tracking</li>
                <li>• Overdue reminders</li>
              </ul>
            </div>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <FileSpreadsheet className="w-5 h-5" />
            Accounting Integration
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <p className="text-sm text-muted-foreground mb-4">
            Sync your financial data with popular accounting software:
          </p>
          <div className="space-y-3">
            <div className="flex items-start gap-3">
              <div className="w-8 h-8 bg-green-100 rounded flex items-center justify-center">
                <span className="text-xs font-bold text-green-600">QB</span>
              </div>
              <div>
                <h4 className="font-semibold">QuickBooks Online</h4>
                <p className="text-sm text-muted-foreground">
                  Two-way sync of invoices and customers
                </p>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <div className="w-8 h-8 bg-blue-100 rounded flex items-center justify-center">
                <span className="text-xs font-bold text-blue-600">X</span>
              </div>
              <div>
                <h4 className="font-semibold">Xero</h4>
                <p className="text-sm text-muted-foreground">Complete accounting synchronization</p>
              </div>
            </div>
          </div>
          <div className="mt-4 p-4 bg-muted rounded-lg">
            <p className="text-sm font-semibold mb-2">What Syncs:</p>
            <div className="grid grid-cols-2 gap-2 text-sm text-muted-foreground">
              <div>• Invoices</div>
              <div>• Payments</div>
              <div>• Customers</div>
              <div>• Products/Services</div>
              <div>• Expenses</div>
              <div>• Tax rates</div>
            </div>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <TrendingUp className="w-5 h-5" />
            Financial Analytics
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <p className="text-sm text-muted-foreground">
            Access comprehensive financial insights and reporting:
          </p>
          <div className="space-y-3">
            <div className="flex items-start gap-3">
              <Badge variant="outline">Revenue</Badge>
              <div>
                <p className="text-sm font-semibold">Revenue Analytics</p>
                <p className="text-sm text-muted-foreground">
                  Track revenue by client, service, and time period
                </p>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <Badge variant="outline">Profitability</Badge>
              <div>
                <p className="text-sm font-semibold">Project Profitability</p>
                <p className="text-sm text-muted-foreground">
                  Analyze margins and identify your most profitable services
                </p>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <Badge variant="outline">Cash Flow</Badge>
              <div>
                <p className="text-sm font-semibold">Cash Flow Forecasting</p>
                <p className="text-sm text-muted-foreground">
                  Predict future cash positions based on pipeline and history
                </p>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <Badge variant="outline">Collections</Badge>
              <div>
                <p className="text-sm font-semibold">Aging Reports</p>
                <p className="text-sm text-muted-foreground">
                  Monitor outstanding invoices and collection efficiency
                </p>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <PieChart className="w-5 h-5" />
            Financial Dashboard
          </CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-muted-foreground mb-4">Key financial metrics at a glance:</p>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="text-center p-3 border rounded">
              <p className="text-sm text-muted-foreground">Monthly Revenue</p>
              <p className="text-xl font-bold text-primary">$125,000</p>
            </div>
            <div className="text-center p-3 border rounded">
              <p className="text-sm text-muted-foreground">Outstanding</p>
              <p className="text-xl font-bold text-yellow-600">$45,000</p>
            </div>
            <div className="text-center p-3 border rounded">
              <p className="text-sm text-muted-foreground">Avg Collection</p>
              <p className="text-xl font-bold text-green-600">28 days</p>
            </div>
            <div className="text-center p-3 border rounded">
              <p className="text-sm text-muted-foreground">Profit Margin</p>
              <p className="text-xl font-bold text-blue-600">42%</p>
            </div>
          </div>
          <div className="flex items-start gap-2 mt-4">
            <Lightbulb className="w-4 h-4 text-yellow-500 mt-1" />
            <p className="text-sm text-muted-foreground">
              Access the full financial dashboard from Analytics → Financial Dashboard
            </p>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

// Testing Checklist Content
function TestingChecklistContent() {
  return (
    <div className="p-6 space-y-6">
      <div>
        <h2 className="text-2xl font-bold mb-4">Testing Checklist for Team Feedback ✅</h2>
        <p className="text-lg mb-4">
          Help us improve\! Test these features and let us know what you think.
        </p>
        <div className="bg-green-50 dark:bg-green-950 p-4 rounded-lg border border-green-200 dark:border-green-800">
          <p className="text-sm">
            <strong>How to provide feedback:</strong> As you test each item, note any issues or suggestions. 
            Share your feedback with the development team through your manager or in our team chat.
          </p>
        </div>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Layers className="w-5 h-5" />
            Core Features to Test
          </CardTitle>
          <CardDescription>
            Try each of these workflows and see if they work smoothly
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          <div className="space-y-4">
            <h4 className="font-semibold flex items-center gap-2">
              <Users className="w-4 h-4" />
              Contact Management
            </h4>
            <div className="space-y-2 ml-6">
              <label className="flex items-center gap-2 text-sm">
                <input type="checkbox" className="rounded" />
                <span>Create a new contact with all fields filled</span>
              </label>
              <label className="flex items-center gap-2 text-sm">
                <input type="checkbox" className="rounded" />
                <span>Edit an existing contact</span>
              </label>
              <label className="flex items-center gap-2 text-sm">
                <input type="checkbox" className="rounded" />
                <span>Search for a contact by name</span>
              </label>
              <label className="flex items-center gap-2 text-sm">
                <input type="checkbox" className="rounded" />
                <span>Filter contacts by organization</span>
              </label>
              <label className="flex items-center gap-2 text-sm">
                <input type="checkbox" className="rounded" />
                <span>Add a note to a contact</span>
              </label>
            </div>
          </div>

          <Separator />

          <div className="space-y-4">
            <h4 className="font-semibold flex items-center gap-2">
              <Building2 className="w-4 h-4" />
              Organization Management
            </h4>
            <div className="space-y-2 ml-6">
              <label className="flex items-center gap-2 text-sm">
                <input type="checkbox" className="rounded" />
                <span>Create a new organization</span>
              </label>
              <label className="flex items-center gap-2 text-sm">
                <input type="checkbox" className="rounded" />
                <span>Link a contact to an organization</span>
              </label>
              <label className="flex items-center gap-2 text-sm">
                <input type="checkbox" className="rounded" />
                <span>View organization hierarchy (parent/child companies)</span>
              </label>
              <label className="flex items-center gap-2 text-sm">
                <input type="checkbox" className="rounded" />
                <span>Update organization status (active/inactive)</span>
              </label>
            </div>
          </div>

          <Separator />

          <div className="space-y-4">
            <h4 className="font-semibold flex items-center gap-2">
              <Target className="w-4 h-4" />
              Opportunity Tracking
            </h4>
            <div className="space-y-2 ml-6">
              <label className="flex items-center gap-2 text-sm">
                <input type="checkbox" className="rounded" />
                <span>Create a new opportunity/deal</span>
              </label>
              <label className="flex items-center gap-2 text-sm">
                <input type="checkbox" className="rounded" />
                <span>Move opportunity through pipeline stages</span>
              </label>
              <label className="flex items-center gap-2 text-sm">
                <input type="checkbox" className="rounded" />
                <span>Update opportunity value and probability</span>
              </label>
              <label className="flex items-center gap-2 text-sm">
                <input type="checkbox" className="rounded" />
                <span>Mark opportunity as won/lost</span>
              </label>
            </div>
          </div>

          <Separator />

          <div className="space-y-4">
            <h4 className="font-semibold flex items-center gap-2">
              <DollarSign className="w-4 h-4" />
              Invoice Management
            </h4>
            <div className="space-y-2 ml-6">
              <label className="flex items-center gap-2 text-sm">
                <input type="checkbox" className="rounded" />
                <span>Create a new invoice</span>
              </label>
              <label className="flex items-center gap-2 text-sm">
                <input type="checkbox" className="rounded" />
                <span>Add line items to invoice</span>
              </label>
              <label className="flex items-center gap-2 text-sm">
                <input type="checkbox" className="rounded" />
                <span>Generate PDF of invoice</span>
              </label>
              <label className="flex items-center gap-2 text-sm">
                <input type="checkbox" className="rounded" />
                <span>Send invoice via email</span>
              </label>
            </div>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <AlertCircle className="w-5 h-5" />
            Things to Look Out For
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-3">
            <div className="flex items-start gap-3">
              <Badge variant="destructive">Bug</Badge>
              <div className="flex-1">
                <p className="text-sm font-semibold">Error Messages</p>
                <p className="text-xs text-muted-foreground">
                  Do you see any red error messages? Take a screenshot\!
                </p>
              </div>
            </div>
            
            <div className="flex items-start gap-3">
              <Badge variant="warning" className="bg-yellow-500">Slow</Badge>
              <div className="flex-1">
                <p className="text-sm font-semibold">Performance Issues</p>
                <p className="text-xs text-muted-foreground">
                  Does anything feel slow or take too long to load?
                </p>
              </div>
            </div>
            
            <div className="flex items-start gap-3">
              <Badge variant="secondary">UX</Badge>
              <div className="flex-1">
                <p className="text-sm font-semibold">Confusing Features</p>
                <p className="text-xs text-muted-foreground">
                  Is anything hard to find or understand?
                </p>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      <Card className="border-primary">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Mail className="w-5 h-5" />
            How to Report Your Findings
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            <p className="text-sm">When reporting an issue, please include:</p>
            <ol className="list-decimal list-inside space-y-2 text-sm text-muted-foreground">
              <li>What you were trying to do</li>
              <li>What actually happened</li>
              <li>Any error messages you saw (screenshots help\!)</li>
              <li>Which browser you're using (Chrome, Safari, etc.)</li>
            </ol>
            
            <div className="bg-blue-50 dark:bg-blue-950 p-4 rounded-lg mt-4">
              <p className="text-sm">
                <strong>Example Report:</strong><br />
                "I tried to create a new invoice for ABC Hotel. When I clicked Save, 
                nothing happened and the button just kept spinning. Using Chrome on Mac."
              </p>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}